import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOL = { USD: "$", GBP: "GBP ", EUR: "EUR ", AED: "AED " };
const fmt = (amount, cur) =>
  `${CURRENCY_SYMBOL[cur] || ""}${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayInvoicePage() {
  const { invoiceNumber } = useParams();
  const [sp] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);

  const returnedStatus = sp.get("status");
  const returnedSessionId = sp.get("session_id");

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/public/${invoiceNumber}`);
      setInvoice(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? "Invoice not found" : "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceNumber]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // Poll when returning from Stripe
  useEffect(() => {
    if (returnedStatus !== "success" || !returnedSessionId) return;
    let attempts = 0;
    let cancelled = false;
    setPolling(true);

    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/invoices/public/${invoiceNumber}/status?session_id=${returnedSessionId}`);
        if (cancelled) return;
        if (res.data.status === "paid") {
          setPolling(false);
          fetchInvoice();
          return;
        }
      } catch (error) {
        // Polling failure is expected while Stripe is still finalising payment; keep retrying.
        console.debug("pay-poll retry:", error?.response?.status || error.message);
      }
      if (attempts < 8 && !cancelled) setTimeout(poll, 2000);
      else setPolling(false);
    };
    poll();
    return () => { cancelled = true; };
  }, [returnedStatus, returnedSessionId, invoiceNumber, fetchInvoice]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-[#B8860B]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white border border-gray-200 p-8 max-w-md text-center">
          <AlertCircle className="mx-auto text-red-500 mb-3" size={40} />
          <h1 className="font-display text-xl mb-2">{error}</h1>
          <p className="text-sm text-gray-500">Please check the invoice number or contact office@magnuskor.com</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === "paid";
  const isCancelled = invoice.status === "cancelled";
  const canPay = !isPaid && !isCancelled && invoice.stripe_checkout_url;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="font-display text-2xl tracking-wide">MAGNUS KOR</div>
          <div className="text-[#B8860B] text-xs tracking-widest mt-1">TALENT PARTNERS</div>
        </div>

        <div className="bg-white border-t-4 border-[#B8860B] shadow-sm" data-testid="pay-invoice-card">
          {/* Status banner */}
          {isPaid && (
            <div className="bg-green-50 border-b border-green-200 px-6 py-4 flex items-center gap-3 text-green-800">
              <CheckCircle2 size={22} />
              <div>
                <div className="font-medium">Payment received</div>
                <div className="text-xs">Paid on {(invoice.paid_at || "").slice(0, 10)} — thank you.</div>
              </div>
            </div>
          )}
          {isCancelled && (
            <div className="bg-gray-100 border-b border-gray-200 px-6 py-4 text-gray-700 text-sm">This invoice has been cancelled.</div>
          )}
          {polling && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-center gap-2 text-yellow-800 text-sm">
              <Loader2 className="animate-spin" size={16} /> Confirming your payment…
            </div>
          )}
          {returnedStatus === "cancelled" && !polling && !isPaid && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 text-yellow-800 text-sm">
              Payment was cancelled. You can try again below.
            </div>
          )}

          <div className="px-6 sm:px-8 py-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">Invoice</div>
                <div className="font-display text-lg">{invoice.invoice_number}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">Due</div>
                <div className="text-sm">{invoice.due_date}</div>
              </div>
            </div>

            <div className="mb-6 text-sm">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Billed To</div>
              <div>{invoice.client_company || invoice.client_name}</div>
              {invoice.client_company && <div className="text-gray-500">{invoice.client_name}</div>}
            </div>

            <div className="border-t border-b border-gray-200 py-4 mb-4">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Description</div>
              <div className="text-sm">{invoice.description}</div>
            </div>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span><span>{fmt(invoice.amount, invoice.currency)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>VAT</span><span>{fmt(invoice.vat_amount, invoice.currency)}</span>
              </div>
              <div className="flex justify-between font-display text-xl pt-2 border-t border-gray-200 mt-2">
                <span>Total</span>
                <span className="text-[#B8860B]" data-testid="pay-total">{fmt(invoice.total_amount, invoice.currency)}</span>
              </div>
            </div>

            {canPay && (
              <a
                href={invoice.stripe_checkout_url}
                className="block text-center bg-[#B8860B] hover:bg-[#9A7209] text-white font-medium tracking-wider py-4 mt-6 transition-colors"
                data-testid="pay-now-btn"
              >
                PAY {fmt(invoice.total_amount, invoice.currency)} NOW
              </a>
            )}
            {canPay && (
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-3">
                <Lock size={12} /> Secure payment via Stripe · Card · Apple Pay · Google Pay
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-xs text-gray-500 mt-6">
          Questions? <a href="mailto:office@magnuskor.com" className="text-[#B8860B]">office@magnuskor.com</a>
        </div>
      </div>
    </div>
  );
}
